import {
  forwardRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import * as S from './EditorTextBoxToolbar.styled'

/** The gap between an anchor's bottom edge and the popover hung from it. */
export const POPOVER_GAP_PX = 4
/** The margin a popover keeps from the window's edges. */
const POPOVER_VIEWPORT_MARGIN_PX = 8

export type FloatingPopoverProps = Omit<
  ComponentProps<typeof S.Popover>,
  '$floating'
>

/**
 * A popover rendered into the body at a fixed viewport position, so no
 * ancestor can clip it: the canvas is a scroll container that cut off any
 * toolbar popover reaching past its edge, and a modal's dialog scrolls and
 * cut off the style bar's. `$layer` says what it must paint above: the
 * canvas's panels, or a modal's backdrop as well.
 */
export const FloatingPopover = forwardRef<HTMLDivElement, FloatingPopoverProps>(
  ({ children, ...rest }, ref) =>
    typeof document === 'undefined'
      ? null
      : createPortal(
          <S.Popover ref={ref} $floating {...rest}>
            {children}
          </S.Popover>,
          document.body,
        ),
)
FloatingPopover.displayName = 'FloatingPopover'

/**
 * Where a popover wants to hang, in viewport px: its top-left corner below
 * its anchor, and, when it cannot fit below, the anchor's top edge to hang
 * above instead (`flipTop`, the popover's bottom lands a gap above it).
 */
export type FloatingAnchor = { left: number; top: number; flipTop?: number }

/**
 * Keeps an open popover at its anchor and inside the window: shifted left
 * only as far as the window's right edge demands, flipped above its anchor
 * when it would pass the window's bottom and there is more room above, its
 * list capped at the window's bottom (`--editor-menu-max-h`) either way.
 * `anchor` is read on every fit,
 * so it follows whatever it is taken from. Re-measured on open, whenever
 * the popover's own content changes size (a group expanding, a hex view
 * replacing the tiles), whenever `observed` changes size (a toolbar
 * wrapping), on a window resize and on any scroll, since the anchor moves
 * with the surface it sits on. The popover stays invisible until the first
 * fit has placed it, so it never paints at the window's corner for a frame.
 */
export const useFloatingPopoverPlacement = (
  /** Which popover is open (the effect re-fits when it changes), or nothing. */
  open: string | boolean | null,
  popoverRef: RefObject<HTMLDivElement | null>,
  anchor: () => FloatingAnchor | null,
  observed: RefObject<HTMLElement | null> | null = null,
): CSSProperties => {
  const [pos, setPos] = useState<FloatingAnchor | null>(null)
  const [room, setRoom] = useState<number | null>(null)
  const [maxHeight, setMaxHeight] = useState<number | null>(null)
  const anchorRef = useRef(anchor)
  anchorRef.current = anchor

  useLayoutEffect(() => {
    const popover = popoverRef.current
    if (!open || !popover) {
      setPos(null)
      return undefined
    }
    const fit = () => {
      const wanted = anchorRef.current()
      if (!wanted) return
      const rect = popover.getBoundingClientRect()
      const margin = POPOVER_VIEWPORT_MARGIN_PX
      const maxLeft = Math.floor(window.innerWidth - margin - rect.width)
      const left = Math.max(margin, Math.min(Math.round(wanted.left), maxLeft))
      // The side with room: below the anchor, or above it when the popover
      // would pass the window's bottom and there is more room above. The
      // popover is capped to that room in the same pass it is placed, so it
      // never reaches over its anchor while its content is still shrinking.
      const roomBelow = Math.floor(window.innerHeight - margin - wanted.top)
      const roomAbove =
        wanted.flipTop === undefined
          ? 0
          : Math.floor(wanted.flipTop - POPOVER_GAP_PX - margin)
      const flip =
        wanted.flipTop !== undefined &&
        rect.height > roomBelow &&
        roomAbove > roomBelow
      const room = Math.max(96, flip ? roomAbove : roomBelow)
      const height = Math.min(rect.height, room)
      const top = Math.max(
        margin,
        Math.round(
          flip
            ? (wanted.flipTop as number) - POPOVER_GAP_PX - height
            : wanted.top,
        ),
      )
      setPos((prev) =>
        prev && prev.left === left && prev.top === top ? prev : { left, top },
      )
      setRoom((prev) => (prev === room ? prev : room))
      const style = getComputedStyle(popover)
      const padding =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
      const next = Math.max(96, Math.floor(room - padding))
      setMaxHeight((prev) => (prev === next ? prev : next))
    }
    fit()
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null
    ro?.observe(popover)
    const watched = observed?.current ?? null
    if (watched) ro?.observe(watched)
    window.addEventListener('resize', fit)
    document.addEventListener('scroll', fit, true)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', fit)
      document.removeEventListener('scroll', fit, true)
    }
  }, [open, popoverRef, observed])

  return useMemo(
    () =>
      ({
        '--editor-popover-left': `${pos?.left ?? 0}px`,
        '--editor-popover-top': `${pos?.top ?? 0}px`,
        '--editor-popover-max-h': room === null ? undefined : `${room}px`,
        '--editor-menu-max-h':
          maxHeight === null ? undefined : `${maxHeight}px`,
        visibility: pos === null ? 'hidden' : undefined,
      }) as CSSProperties,
    [pos, room, maxHeight],
  )
}
